import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const WIPCommentModal = ({
  open,
  onClose,
  onSave,
  rowData,
  fieldName,
  approvalInfo,
  currentUserId, // Pass user ID from parent
  currentUserName,
  readOnly = false, // If true, users can only view comments, not add/delete
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    if (open && rowData) {
      const existingComments = rowData[fieldName] || [];
      // Ground-up implementation: Comments are always arrays of objects with userId
      let commentsArray = [];
      if (Array.isArray(existingComments)) {
        commentsArray = existingComments;
      }
      setComments(commentsArray);
      setNewComment("");
    }
  }, [open, rowData, fieldName]);

  const handleAddComment = () => {
    if (newComment.trim()) {
      const commentObj = {
        userId: currentUserId, // Changed from authorId to userId per API spec
        userName: currentUserName,
        createdAt: new Date().toISOString(),
        comment: newComment.trim(),
      };
      console.log("🔵 [Modal] handleAddComment");
      console.log("  Current comments:", comments);
      console.log("  New comment obj:", commentObj);
      const updatedComments = [...comments, commentObj];
      console.log("  Updated comments:", updatedComments);
      setComments(updatedComments);
      setNewComment("");
    }
  };

  const handleDeleteComment = (index) => {
    setComments(comments.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    console.log("🟢 [Modal] handleSave");
    console.log("  Sending comments to parent:", comments);
    console.log("  Comments count:", comments.length);
    console.log("  Comments structure:", JSON.stringify(comments, null, 2));
    // Return array of comment objects
    onSave(comments);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {approvalInfo?.title || "View Comments"}
          </Typography>
          <Typography variant="subtitle">
            {"Latest comment is towards the bottom of the list"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* REQ and ACTUAL Information */}
        {approvalInfo && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#F9FAFB", borderRadius: "8px" }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
            >
              Approval Information
            </Typography>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
            >
              {approvalInfo.reqDate && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", fontWeight: 500 }}
                  >
                    REQ Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {approvalInfo.reqDate}
                  </Typography>
                </Box>
              )}
              {approvalInfo.actualStatus !== undefined && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", fontWeight: 500 }}
                  >
                    Status
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color:
                        approvalInfo.actualStatus === "Approved"
                          ? "#22C55E"
                          : approvalInfo.actualStatus === "Rejected"
                          ? "#EF4444"
                          : "#F59E0B",
                    }}
                  >
                    {approvalInfo.actualStatus || "Not Set"}
                  </Typography>
                </Box>
              )}
              {approvalInfo.actualDate && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", fontWeight: 500 }}
                  >
                    ACTUAL Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {approvalInfo.actualDate}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Existing Comments */}
        {comments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
            >
              Comments ({comments.length})
            </Typography>
            <List sx={{ p: 0 }}>
              {comments.map((commentObj, index) => {
                // Ground-up implementation: Comments are always objects with userId
                const commentText = commentObj.comment;
                const userId = commentObj?.userName || commentObj.userId;
                const createdAt = commentObj.createdAt;

                return (
                  <ListItem
                    key={index}
                    sx={{
                      mb: 1,
                      bgcolor: "#F9FAFB",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        {/* Comment metadata */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            mb: 0.5,
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#6B7280", fontWeight: 600 }}
                          >
                            {userId || "Unknown User"}
                          </Typography>
                          {createdAt && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ color: "#D1D5DB" }}
                              >
                                •
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#9CA3AF" }}
                              >
                                {new Date(createdAt).toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </Typography>
                            </>
                          )}
                        </Box>
                        {/* Comment text */}
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#111827" }}
                        >
                          {commentText}
                        </Typography>
                      </Box>
                      {/* {!readOnly && (
                        <IconButton
                          onClick={() => handleDeleteComment(index)}
                          size="small"
                          sx={{
                            color: "#EF4444",
                            ml: 1,
                            "&:hover": { bgcolor: "#FEE2E2" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )} */}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {!readOnly && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Add New Comment */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}
              >
                Add New Comment
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your comment here..."
                  variant="outlined"
                  disabled={readOnly}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      handleAddComment();
                    }
                  }}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  borderRadius: "8px",
                  bgcolor: "#4B68D4",
                  "&:hover": {
                    bgcolor: "#3B52B4",
                  },
                }}
              >
                Add Comment
              </Button>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, color: "#6B7280" }}
              >
                Tip: Press Cmd/Ctrl + Enter to add comment
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        {readOnly ? (
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: "#4B68D4",
              "&:hover": {
                bgcolor: "#3B52B4",
              },
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              sx={{
                textTransform: "none",
                color: "#6B7280",
                "&:hover": {
                  bgcolor: "#F3F4F6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                textTransform: "none",
                bgcolor: "#10B981",
                "&:hover": {
                  bgcolor: "#059669",
                },
              }}
            >
              Save Comments
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WIPCommentModal;
